/**
 * iter267 — Sélection de photos depuis la bibliothèque pour les rattacher à un OR.
 * Par défaut on propose les photos du véhicule du dossier, avec repli sur toute
 * la bibliothèque si besoin.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Check, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function OrPhotoLibraryDialog({ open, onClose, orId, vehiculeId, onAttached, onPick, titre }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState([]);
  const [toutes, setToutes] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/photos", {
        params: !toutes && vehiculeId ? { vehicule_id: vehiculeId } : {},
      });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [open, toutes, vehiculeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!open) { setSelection([]); setToutes(false); } }, [open]);

  const basculer = (pid) => setSelection((s) => (s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]));

  const attacher = async () => {
    setBusy(true);
    try {
      // Mode « sélection » : le parent décide quoi faire des photos choisies
      if (onPick) {
        await onPick(selection);
        onClose();
        return;
      }
      const { data } = await api.post("/photos/attacher-or", {
        or_id: orId, photo_ids: selection, type: "depose",
      });
      toast.success(`${data.added} photo(s) ajoutée(s) au dossier`);
      onAttached?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rattachement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="or-photo-library-dialog">
        <DialogHeader>
          <DialogTitle>{titre || "Photos de la bibliothèque"}</DialogTitle>
          <DialogDescription>
            {toutes || !vehiculeId ? "Toute la bibliothèque" : "Photos du véhicule de ce dossier"}
          </DialogDescription>
        </DialogHeader>

        {vehiculeId && (
          <div>
            <Button
              variant="outline" size="sm"
              onClick={() => setToutes((v) => !v)}
              data-testid="or-photo-library-toggle"
            >
              {toutes ? "Voir seulement ce véhicule" : "Voir toute la bibliothèque"}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : !items.length ? (
          <div className="py-10 text-center text-sm text-gray-500" data-testid="or-photo-library-empty">
            <ImageIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            Aucune photo disponible. Prenez-les depuis l&apos;onglet « Photos » du menu.
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[55vh] overflow-y-auto content-start auto-rows-max">
            {items.map((p) => {
              const choisie = selection.includes(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => basculer(p.id)}
                  className={`relative rounded-md overflow-hidden border-2 transition-all ${choisie ? "border-[#52a675]" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid={`or-photo-library-item-${p.id}`}
                >
                  <img src={p.thumb} alt="" className="w-full h-24 object-cover" />
                  <div className="px-1 py-0.5 text-[10px] text-gray-600 truncate bg-white">
                    {p.plaque || "À classer"}
                  </div>
                  {choisie && (
                    <span className="absolute top-1 right-1 bg-[#52a675] text-white rounded-full p-0.5">
                      <Check className="w-3 h-3" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="or-photo-library-cancel">Annuler</Button>
          <Button
            onClick={attacher}
            disabled={!selection.length || busy}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="or-photo-library-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Ajouter {selection.length ? `(${selection.length})` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
